import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { LoaderComponent } from '@components/app-loader/app-loader.component';
import { AppRoutes } from '@helpers/app-routes';
import { HttpError } from '@models/http-error.model';
import { AuthService } from '@services/auth.service';
import {
  passwordPolicyValidator,
  passwordsMatchValidator,
} from '@validators/password.validator';

const BAD_REQUEST_STATUS = 400;
const INVALID_TOKEN_MESSAGE = 'errors.token_invalid';

@Component({
  selector: 'app-reset-password-page',
  imports: [ReactiveFormsModule, RouterLink, TranslateModule, LoaderComponent],
  templateUrl: './reset-password-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class ResetPasswordPageComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  readonly token = input.required<string>();

  protected readonly loginRoute = `/${AppRoutes.login}`;
  protected readonly requestRoute = `/${AppRoutes.resetPasswordRequest}`;

  protected readonly form = this.fb.nonNullable.group(
    {
      password: ['', [Validators.required, passwordPolicyValidator]],
      confirmation: ['', Validators.required],
    },
    { validators: passwordsMatchValidator('password', 'confirmation') }
  );
  protected readonly isSubmitting = signal(false);
  protected readonly tokenInvalid = signal(false);
  protected readonly showPassword = signal(false);
  protected readonly showConfirmation = signal(false);

  togglePasswordVisibility(): void {
    this.showPassword.update(value => !value);
  }

  toggleConfirmationVisibility(): void {
    this.showConfirmation.update(value => !value);
  }

  submit(): void {
    if (this.isSubmitting()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    this.authService
      .resetPassword({
        token: this.token(),
        password: this.form.getRawValue().password,
      })
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant('reset_password.reset.toast.success_message'),
            this.translate.instant('reset_password.reset.toast.success_title')
          );
          void this.router.navigate([AppRoutes.login]);
        },
        error: (err: HttpError) => this.#handleError(err),
      });
  }

  #handleError(err: HttpError): void {
    this.isSubmitting.set(false);

    if (
      err.status === BAD_REQUEST_STATUS &&
      err.message === INVALID_TOKEN_MESSAGE
    ) {
      this.tokenInvalid.set(true);
      return;
    }

    this.toastr.error(
      this.translate.instant('reset_password.reset.errors.generic'),
      this.translate.instant('reset_password.reset.errors.title')
    );
  }
}
